import { HTTPError, fetchWithRetry, isNilOrEmptyString, } from '@tunein/web-utils';
import { flow, identity, isBrowser, isNil, isPlainObject, mapKeys, noop, omitBy, pick, } from 'es-toolkit';
import { toLower } from 'es-toolkit/compat';
import { EventEmitter } from 'eventemitter3';
import { OpmlError } from '../errors';
import { camelCase } from '../utils';
import { GraphQLClient } from './GraphQLClient';
import { legacyTransformOpmlResponse, transformOpmlResponse } from './utils';
const TUNEIN_REFRESH_TOKEN = 'tirt';
function mapKeysBy(object, getNewKey) {
    return mapKeys(object, (_, key) => getNewKey(key));
}
export class RequestClient extends EventEmitter {
    #storeClient;
    #baseParams;
    #authTokens = {};
    #headers = {
        'content-type': 'application/json',
        ...(!isBrowser() && { allow_insecure_auth: 'true' }),
    };
    #graphQLClient;
    #reauthPromise = null;
    constructor(store) {
        super();
        const options = store.options;
        this.#storeClient = store;
        this.#baseParams = omitBy({
            itemUrlScheme: 'secure',
            ...pick(options, ['serial', 'partnerId', 'version']),
            ...(options.optionalParams && options.optionalParams),
        }, isNil);
        this.updateHeaders(options.requestHeaders);
        this.#setAuthTokens(options.authTokens || this.#authTokens);
        if (options.ipAddress) {
            this.updateHeaders({ 'x-forwarded-for': options.ipAddress });
        }
        this.#graphQLClient = new GraphQLClient(options.domains.graphQl, this.fetch);
    }
    get #globalReauthPromise() {
        return !isBrowser() ? Promise.resolve() : window.__TI_REAUTH_PROMISE__;
    }
    set #globalReauthPromise(promise) {
        if (isBrowser()) {
            window.__TI_REAUTH_PROMISE__ = promise;
        }
    }
    #isOpmlUrl(url) {
        return url.toString().startsWith(this.#storeClient.options.domains.opml);
    }
    #removeHeader(key) {
        delete this.#headers[key];
    }
    #setAuthTokens(authTokens) {
        this.#authTokens = authTokens ?? {};
        if (this.#authTokens.accessToken) {
            this.updateHeaders({
                authorization: `Bearer ${this.#authTokens.accessToken}`,
            });
        }
        else {
            this.#removeHeader('authorization');
        }
        this.emit('authTokensUpdated', this.#authTokens);
    }
    #buildHeaders(headers) {
        return {
            ...mapKeysBy(this.#headers, toLower),
            ...(headers && mapKeysBy(headers, toLower)),
        };
    }
    #buildUrl(urlString, params) {
        const url = isBrowser()
            ? new URL(urlString, window.location.href)
            : new URL(urlString);
        const searchParams = {
            ...(params && omitBy(params, isNilOrEmptyString)),
            ...this.#baseParams,
            ...(this.#isOpmlUrl(url) && {
                render: 'json',
            }),
        };
        url.search = new URLSearchParams(searchParams).toString();
        return url.toString();
    }
    #proxyReauth() {
        const { domains: { local }, proxyReauthMode, } = this.#storeClient.options;
        const headers = {};
        if (proxyReauthMode === 'gemini-web') {
            headers[TUNEIN_REFRESH_TOKEN] = this.#authTokens.refreshToken;
        }
        return fetchWithRetry(this.#buildUrl(`${local}/auth/reauth`), {
            method: 'POST',
            headers: this.#buildHeaders(headers),
            retries: 0,
        });
    }
    async #platformReauth() {
        const { domains: { opml }, partnerId, partnerSecret, } = this.#storeClient.options;
        const rawAuthTokens = await fetchWithRetry(this.#buildUrl(`${opml}/oauth/access_token`), {
            method: 'POST',
            headers: this.#buildHeaders({
                'content-type': 'application/x-www-form-urlencoded',
                authorization: `Basic ${btoa(`${partnerId}:${partnerSecret}`)}`,
            }),
            body: new URLSearchParams({
                refreshToken: this.#authTokens.refreshToken,
            }),
            retries: 0,
        });
        return camelCase(rawAuthTokens);
    }
    get authTokens() {
        return this.#authTokens;
    }
    set authTokens(authTokens) {
        this.#setAuthTokens(authTokens);
    }
    get hasAuthTokens() {
        return !!this.#authTokens.accessToken;
    }
    get graphQlSdk() {
        return this.#graphQLClient.sdk;
    }
    updateHeaders(headers) {
        if (headers) {
            Object.assign(this.#headers, mapKeysBy(headers, toLower));
        }
    }
    async reauth() {
        const { promise, resolve, reject } = Promise.withResolvers();
        this.#reauthPromise = promise.then(noop).catch(noop);
        this.#globalReauthPromise = this.#reauthPromise;
        try {
            const authTokens = !isBrowser() || this.#storeClient.options.proxyReauthMode === 'disabled'
                ? await this.#platformReauth()
                : await this.#proxyReauth();
            if (!authTokens?.accessToken) {
                throw new Error('No tokens provided on reauth');
            }
            this.#setAuthTokens(authTokens);
            resolve(this.#authTokens);
        }
        catch (error) {
            this.#setAuthTokens({});
            this.emit('reauthError', error);
            this.emit('error', {
                message: 'Reauth Error',
                context: { error },
            });
            reject(error);
        }
        return promise;
    }
    fetch = async (fetchOptions, skipReauth = false) => {
        const { url, params, skipCamelCaseTransform, transform, headers, parseAs, skipUrlTransform, skipAllResponseTransforms, retries: retriesOverride, ...fetchWithRetryOptions } = fetchOptions;
        const { timeout, retries, domains } = this.#storeClient.options;
        const { accessToken: previousAccessToken } = this.#authTokens;
        await Promise.all([this.#reauthPromise, this.#globalReauthPromise]);
        const finalUrl = skipUrlTransform ? url : this.#buildUrl(url, params);
        const finalOptions = {
            ...fetchWithRetryOptions,
            headers: this.#buildHeaders(headers),
            timeout,
            retries: retriesOverride || retries,
            parseAs,
        };
        const isOpmlUrl = this.#isOpmlUrl(finalUrl);
        if (!isBrowser()) {
            this.emit('debugLog', { label: 'Server Request URL', value: finalUrl });
        }
        const requestStartTime = Date.now();
        const resOrError = await fetchWithRetry(finalUrl, finalOptions).catch(identity);
        const shouldReauthAndRetry = previousAccessToken && resOrError.status === 401 && !skipReauth;
        this.emit('responseMetadata', {
            url: finalUrl,
            method: finalOptions.method || 'GET',
            responseTime: Date.now() - requestStartTime,
            status: resOrError?.head?.status || resOrError?.status || null,
        });
        if (shouldReauthAndRetry) {
            await Promise.all([this.#reauthPromise, this.#globalReauthPromise]);
            if (previousAccessToken === this.#authTokens.accessToken) {
                await this.reauth().catch(noop);
            }
            return this.fetch(fetchOptions, true);
        }
        if (resOrError instanceof Error) {
            const errorHasPayload = resOrError instanceof HTTPError && isPlainObject(resOrError?.body);
            if (errorHasPayload) {
                resOrError.body = camelCase(resOrError.body);
            }
            if (isOpmlUrl && errorHasPayload) {
                throw new OpmlError(resOrError?.body);
            }
            throw resOrError;
        }
        if (skipAllResponseTransforms) {
            return resOrError;
        }
        const transforms = [
            !skipCamelCaseTransform && camelCase,
            isOpmlUrl &&
                (resOrError?.status !== '200'
                    ? transformOpmlResponse
                    : legacyTransformOpmlResponse),
            transform,
        ].filter(Boolean);
        return transforms.length && !(resOrError instanceof Response)
            ? flow(...transforms)(resOrError)
            : resOrError;
    };
}
